import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BnNgIdleService } from 'bn-ng-idle';
import { InlineSVGModule } from 'ng-inline-svg';
import { ClipboardModule } from 'ngx-clipboard';
import { HighlightModule, HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideToastr } from 'ngx-toastr';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { LowerCaseUrlSerializer, MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './app/app.module';
import { AppConfigService } from './app/config/app-config.service';
import { AuthModule } from './app/modules/auth/auth.module';
import { SunBucksHttpErrorInterceptor } from './app/modules/sun-bucks/core/interceptors/http-error.interceptor';
import { SunBucksJwtInterceptor } from './app/modules/sun-bucks/core/interceptors/jwt.interceptor';
import { SunBucksConfigService } from './app/modules/sun-bucks/core/services/sun-bucks-config.service';
import { environment } from './environments/environment';
import { IpHeaderInterceptor } from './app/modules/sun-bucks/core/interceptors/ip-header.interceptor';
import { InlineSvgDirective } from './app/directives/inline-svg.directive';


const appInitializerConfig = (appConfig: AppConfigService) => {
    return () => {
        return appConfig.loadAppConfig();
    };
};

const sunBucksConfigInitializer = (
    configService: SunBucksConfigService
): (() => Promise<void>) => {
    return () => configService.load();
};

const protectedResourceMap = new Map<string, Array<string>>();



export function getBaseUrl() {
    return document.getElementsByTagName('base')[0].href;
}

const providers = [
    { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] }
];

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(MatDialogModule, BrowserModule, HighlightModule, ClipboardModule, ReactiveFormsModule, FormsModule, AppRoutingModule, InlineSVGModule.forRoot(), NgbModule, AuthModule, FontAwesomeModule, MsalModule, InlineSvgDirective),
    provideHttpClient(withInterceptorsFromDi()),
    provideEnvironmentNgxMask(),
    provideToastr({
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
    }),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerConfig,
      multi: true,
      deps: [AppConfigService],
    },
    {
      // Load Sun Bucks API base URL and clientId from GET /api/config before
      // the app bootstraps.  Must complete before any sun-bucks service makes
      // HTTP calls.  Uses HttpBackend to bypass the JWT interceptor chain.
      provide: APP_INITIALIZER,
      useFactory: sunBucksConfigInitializer,
      multi: true,
      deps: [SunBucksConfigService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SunBucksHttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SunBucksJwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IpHeaderInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: ErrorHandler,
      //useClass: GlobalErrorHandler
    },
    ////SplashScreenService,
    ////{ provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    //{
    //  provide: APP_INITIALIZER,
    //  useFactory: appInitializer,
    //  multi: true,
    //  deps: [AuthService, HttpClient],
    //},
    { provide: UrlSerializer, useClass: LowerCaseUrlSerializer },
    BnNgIdleService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerConfig,
      multi: true,
      deps: [AppConfigService],
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
          typescript: () => import('highlight.js/lib/languages/typescript'),
          scss: () => import('highlight.js/lib/languages/scss'),
          json: () => import('highlight.js/lib/languages/json')
        },
      },
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ]
})
  .catch(err => console.log(err));
